package fr.arcplex.network.impl.coherence

import fr.arcplex.network.NetworkConfig
import fr.arcplex.network.cache.apiCache
import fr.arcplex.network.http.Serializer
import fr.arcplex.network.http.createNetworkHttpClient
import fr.arcplex.network.logging.Logger
import fr.arcplex.network.model.Branding
import fr.arcplex.network.model.CoherenceLabels
import fr.arcplex.network.model.CoherenceTheme
import fr.arcplex.network.model.ActionVariant
import fr.arcplex.network.model.ActionKind
import fr.arcplex.network.model.PageColors
import fr.arcplex.network.model.RailKind
import fr.arcplex.network.model.TextKind
import fr.arcplex.network.model.TextStyleStyle
import fr.arcplex.network.model.TextStyles
import fr.arcplex.network.model.Kinds
import fr.arcplex.network.model.Variants
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.request.get
import io.ktor.client.request.headers
import io.ktor.serialization.kotlinx.json.json
import kotlinx.coroutines.CancellationException
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.doubleOrNull
import kotlinx.serialization.json.intOrNull

/**
 * Coherence branding: theme + labels, served from cache when warm and from the
 * network otherwise.
 */
internal class CoherenceThemeApi(private val config: NetworkConfig) {

    private val client: HttpClient = createNetworkHttpClient(config.allowInvalidCertificates) {
        install(ContentNegotiation) {
            json(Serializer.json)
        }
    }

    private companion object {
        private const val THEME_CACHE_KEY = "coherence:theme"
        private const val LABELS_CACHE_KEY = "coherence:labels"
    }

    /** Cached branding only, so the UI can paint with it before/instead of a network hit. */
    suspend fun cached(): Branding {
        var branding = Branding()
        try {
            apiCache.get(THEME_CACHE_KEY)?.let { raw -> parseTheme(raw)?.let { branding = branding.copy(theme = it) } }
        } catch (_: Exception) {
        }
        try {
            apiCache.get(LABELS_CACHE_KEY)?.let { raw -> parseLabels(raw)?.let { branding = branding.copy(labels = it) } }
        } catch (_: Exception) {
        }
        return branding
    }

    suspend fun refreshBranding(): Result<Branding> = try {
        val branding = cached()
        val withTheme = fetchTheme(branding)
        val withLabels = fetchLabels(withTheme)
        Result.success(withLabels)
    } catch (e: CancellationException) {
        throw e
    } catch (e: Exception) {
        Logger.e("CoherenceTheme", "branding refresh failed: ${e.message}")
        Result.failure(e)
    }

    private suspend fun fetchTheme(branding: Branding): Branding {
        return try {
            val json = client.get("${config.coherenceBaseUrl}/api/box/theme/defaults") {
                headers {
                    append("Accept", "application/json")
                    append("X-API-Key", config.apiKey)
                }
            }.body<JsonObject>()
            apiCache.put(THEME_CACHE_KEY, json.toString())
            parseThemeObject(json)?.let { branding.copy(theme = it) } ?: branding
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            Logger.e("CoherenceTheme", "theme fetch failed: ${e.message}")
            return branding
        }
    }

    private suspend fun fetchLabels(branding: Branding): Branding {
        return try {
            val json = client.get("${config.coherenceBaseUrl}/api/box/labels") {
                headers {
                    append("Accept", "application/json")
                    append("X-API-Key", config.apiKey)
                }
            }.body<JsonObject>()
            apiCache.put(LABELS_CACHE_KEY, json.toString())
            parseLabelsObject(json)?.let { branding.copy(labels = it) } ?: branding
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            Logger.e("CoherenceTheme", "labels fetch failed: ${e.message}")
            return branding
        }
    }

    internal fun parseTheme(raw: String): CoherenceTheme? = try {
        (Serializer.json.parseToJsonElement(raw) as? JsonObject)?.let { parseThemeObject(it) }
    } catch (e: Exception) {
        Logger.e("CoherenceTheme", "theme parse failed: ${e.message}")
        null
    }

    internal fun parseLabels(raw: String): CoherenceLabels? = try {
        (Serializer.json.parseToJsonElement(raw) as? JsonObject)?.let { parseLabelsObject(it) }
    } catch (e: Exception) {
        Logger.e("CoherenceTheme", "labels parse failed: ${e.message}")
        null
    }

    private fun parseThemeObject(json: JsonObject): CoherenceTheme? {
        val d = CoherenceTheme.DEFAULT
        val page = json.obj("page")
        val kinds = json.obj("kinds")
        val textKind = kinds?.obj("text")
        val actionKind = kinds?.obj("action")
        val railKind = kinds?.obj("rail")
        val textStyles = json.obj("text_styles")
        val display = textStyles?.obj("display")
        val body = textStyles?.obj("body")
        val primary = json.obj("variants")?.obj("action.primary")

        return CoherenceTheme(
            page = PageColors(
                background = page?.str("background") ?: d.page.background,
                surface = page?.str("surface") ?: d.page.surface,
                text = page?.str("text") ?: d.page.text,
                muted = page?.str("muted") ?: d.page.muted,
                accent = page?.str("accent") ?: d.page.accent
            ),
            kinds = Kinds(
                text = TextKind(
                    size = textKind?.int("size") ?: d.kinds.text.size,
                    color = textKind?.str("color") ?: d.kinds.text.color,
                    weight = textKind?.str("weight") ?: d.kinds.text.weight,
                    align = textKind?.str("align") ?: d.kinds.text.align
                ),
                action = ActionKind(
                    textSize = actionKind?.int("text_size") ?: d.kinds.action.textSize,
                    textColor = actionKind?.str("text_color") ?: d.kinds.action.textColor,
                    borderColor = actionKind?.str("border_color") ?: d.kinds.action.borderColor,
                    radius = actionKind?.int("radius") ?: d.kinds.action.radius,
                    paddingX = actionKind?.int("padding_x") ?: d.kinds.action.paddingX,
                    paddingY = actionKind?.int("padding_y") ?: d.kinds.action.paddingY,
                    bgColor = actionKind?.str("bg_color") ?: d.kinds.action.bgColor
                ),
                rail = RailKind(
                    titleSize = railKind?.int("title_size") ?: d.kinds.rail.titleSize,
                    titleColor = railKind?.str("title_color") ?: d.kinds.rail.titleColor,
                    cardWidth = railKind?.int("card_width") ?: d.kinds.rail.cardWidth,
                    gap = railKind?.int("gap") ?: d.kinds.rail.gap
                )
            ),
            textStyles = TextStyles(
                display = TextStyleStyle(
                    size = display?.int("size") ?: d.textStyles.display.size,
                    weight = display?.str("weight") ?: d.textStyles.display.weight,
                    lineHeight = display?.double("line_height") ?: d.textStyles.display.lineHeight,
                    color = display?.str("color") ?: d.textStyles.display.color
                ),
                body = TextStyleStyle(
                    size = body?.int("size") ?: d.textStyles.body.size,
                    weight = body?.str("weight") ?: d.textStyles.body.weight,
                    lineHeight = body?.double("line_height") ?: d.textStyles.body.lineHeight,
                    color = body?.str("color") ?: d.textStyles.body.color
                )
            ),
            variants = Variants(
                actionPrimary = ActionVariant(
                    bgColor = primary?.str("bg_color") ?: d.variants.actionPrimary.bgColor,
                    textColor = primary?.str("text_color") ?: d.variants.actionPrimary.textColor,
                    borderColor = primary?.str("border_color") ?: d.variants.actionPrimary.borderColor,
                    weight = primary?.str("weight") ?: d.variants.actionPrimary.weight
                )
            )
        )
    }

    private fun parseLabelsObject(json: JsonObject): CoherenceLabels? {
        val d = CoherenceLabels.DEFAULT
        return CoherenceLabels(
            lang = json.str("lang") ?: d.lang,
            rails = json.strMap("rails") ?: d.rails,
            actions = json.strMap("actions") ?: d.actions,
            slots = json.strMap("slots") ?: d.slots,
            contentTypes = json.strMap("content_types") ?: d.contentTypes
        )
    }

    private fun JsonObject.str(key: String): String? =
        (this[key] as? JsonPrimitive)?.contentOrNull

    private fun JsonObject.int(key: String): Int? =
        (this[key] as? JsonPrimitive)?.intOrNull

    private fun JsonObject.double(key: String): Double? =
        (this[key] as? JsonPrimitive)?.doubleOrNull

    private fun JsonObject.obj(key: String): JsonObject? =
        this[key] as? JsonObject

    private fun JsonObject.strMap(key: String): Map<String, String>? =
        (this[key] as? JsonObject)?.entries?.mapNotNull { (k, v) ->
            (v as? JsonPrimitive)?.contentOrNull?.let { k to it }
        }?.toMap()
}
